import api from "@/lib/apiClient";

/**
 * Resolve a short-link `ref` (UUID / order_number / Base44 external_id) to a
 * local order. Used by /o/:ref (OrderShortLink) and /l/:ref (LabelShortLink).
 *
 * Strategy:
 *   1. Try `GET /orders/{ref}` directly (backend may accept the form).
 *   2. Fall back to listing /orders and case-insensitively matching on id /
 *      order_number / external_id.
 *   3. If still not found AND the caller can sync (admin/manager), trigger
 *      `POST /base44/sync` once and repeat step 2. This handles deep-links
 *      from the user's external ordering system that point at orders not yet
 *      pulled into the local DB.
 *
 * Returns: { order, synced } when found, or null when truly not present.
 * `synced` is true if a Base44 sync had to run to find it (callers can use
 * this to show a small "Synced from Base44" toast).
 */
export default async function resolveOrderRef(ref, { canSync = false } = {}) {
  const trimmed = (ref || "").trim();
  if (!trimmed) return null;

  // 1. Direct GET — cheap if backend accepts non-UUID refs.
  try {
    const { data } = await api.get(`/orders/${encodeURIComponent(trimmed)}`);
    if (data?.id) return { order: data, synced: false };
  } catch (err) {
    const code = err?.response?.status;
    // Anything other than not-found / bad-format should propagate so the
    // caller can render an error state.
    if (code && ![400, 404, 422].includes(code)) throw err;
  }

  // 2. List scan.
  const local = await lookupInList(trimmed);
  if (local) return { order: local, synced: false };

  // 3. Maybe Base44 has it but it isn't synced yet. Try once if we're allowed.
  if (canSync) {
    try {
      await api.post("/base44/sync");
    } catch (err) {
      console.warn("resolveOrderRef: Base44 sync failed", err);
      return null;
    }
    const afterSync = await lookupInList(trimmed);
    if (afterSync) return { order: afterSync, synced: true };
  }

  return null;
}

async function lookupInList(ref) {
  const { data } = await api.get("/orders");
  const needle = ref.toLowerCase();
  return (data || []).find((o) =>
    o && (
      String(o.id || "").toLowerCase() === needle ||
      String(o.order_number || "").toLowerCase() === needle ||
      String(o.external_id || "").toLowerCase() === needle
    )
  ) || null;
}
